import { useEffect, useState } from 'react';
import { api } from '../api';
import { Card, Row } from '../components/Card';
import * as fmt from '../format';
import type { Route, ShareLink } from '../models';
import type { Store } from '../store';
import { CheckpointDialog } from './Checkpoint';
import { Dialog } from './Dialog';

/// Everything that changes the walk rather than reports on it, matching the
/// Manage screens in the two native apps.
export function Manage({ store }: { store: Store }) {
  const [creating, setCreating] = useState(false);
  const [editingCheckpoint, setEditingCheckpoint] = useState(false);
  const tracking = store.walk?.targetTracking ?? null;

  return (
    <>
      {store.walk && <WalkSettings store={store} />}

      {store.walk && <SharesSection walkId={store.walk.walkId} />}

      {store.walk && (
        <Card
          wide
          title="Checkpoint"
          action={
            <button onClick={() => setEditingCheckpoint(true)}>
              {tracking ? 'Change' : 'Set'}
            </button>
          }
        >
          {tracking ? (
            <>
              <Row label="At" value={fmt.miles(tracking.miles)} />
              <Row label="Label" value={tracking.label ?? '—'} />
            </>
          ) : (
            <p className="muted">No checkpoint set.</p>
          )}
          <p className="muted">
            An ad-hoc distance worth chasing. It does not affect the walk's own target.
          </p>
        </Card>
      )}

      <Card
        wide
        title="Walks"
        action={<button onClick={() => setCreating(true)}>Start a new walk…</button>}
      >
        {store.walks.length === 0 && <p className="muted">No walks yet.</p>}
        <ul className="walks">
          {store.walks.map((walk) => (
            <li key={walk.walkId}>
              <button className="walks__item" onClick={() => void store.select(walk)}>
                <span aria-hidden>{walk.walkId === store.walk?.walkId ? '●' : '○'}</span>
                <span>
                  <strong>{walk.name}</strong>
                  <br />
                  <span className="muted">
                    {fmt.mediumDate(walk.startDate)} · {fmt.miles(walk.distanceMiles, 0)} in{' '}
                    {walk.targetDays} days
                  </span>
                </span>
                {(walk.status ?? 'active') === 'complete' && (
                  <span className="muted">Complete</span>
                )}
              </button>
            </li>
          ))}
        </ul>
        <p className="muted">Completed walks stay readable — selecting one shows its history.</p>
      </Card>

      {creating && <NewWalkDialog store={store} onClose={() => setCreating(false)} />}
      {editingCheckpoint && (
        <CheckpointDialog store={store} onClose={() => setEditingCheckpoint(false)} />
      )}
    </>
  );
}

function WalkSettings({ store }: { store: Store }) {
  const walk = store.walk!;
  const [name, setName] = useState(walk.name);
  const [startDate, setStartDate] = useState(walk.startDate);
  const [targetDays, setTargetDays] = useState(String(walk.targetDays));
  const [distanceMiles, setDistanceMiles] = useState(String(walk.distanceMiles));
  const [stepsPerMile, setStepsPerMile] = useState(String(walk.stepsPerMile));
  const [stepsPerMinute, setStepsPerMinute] = useState(String(walk.stepsPerMinute));
  const [status, setStatus] = useState(walk.status ?? 'active');
  const [routeId, setRouteId] = useState(walk.routeId ?? '');
  const [routes, setRoutes] = useState<Route[]>([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset when a different walk is selected.
  useEffect(() => {
    void api.routes().then(setRoutes).catch(() => setRoutes([]));
  }, []);

  useEffect(() => {
    setName(walk.name);
    setStartDate(walk.startDate);
    setTargetDays(String(walk.targetDays));
    setDistanceMiles(String(walk.distanceMiles));
    setStepsPerMile(String(walk.stepsPerMile));
    setStepsPerMinute(String(walk.stepsPerMinute));
    setStatus(walk.status ?? 'active');
    setRouteId(walk.routeId ?? '');
    setSaved(false);
    setError(null);
  }, [walk]);

  const numbers = {
    targetDays: Number(targetDays),
    distanceMiles: Number(distanceMiles),
    stepsPerMile: Number(stepsPerMile),
    stepsPerMinute: Number(stepsPerMinute),
  };
  const valid =
    name !== '' && numbers.targetDays >= 1 && numbers.distanceMiles >= 0.1
    && numbers.stepsPerMile >= 1 && numbers.stepsPerMinute >= 1;

  /// Applying a route to a walk that was set up by hand. The distance follows
  /// the route, and the server refuses one shorter than the ground already
  /// covered - so the only feedback needed here is to show what it says.
  function applyRoute(id: string) {
    setRouteId(id);
    setSaved(false);
    const chosen = routes.find((route) => route.routeId === id);
    if (chosen) setDistanceMiles(String(chosen.distanceMiles));
  }

  async function save() {
    if (!valid) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      // `PUT /config` replaces rather than patches, so every field goes -
      // including the checkpoint and status, which a half-filled body would
      // silently clear.
      await api.updateConfig(walk.walkId, {
        name,
        startDate,
        ...numbers,
        targetTracking: walk.targetTracking ?? null,
        status,
        routeId: routeId === '' ? null : routeId,
      });
      await store.reload();
      setSaved(true);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card wide title="Walk settings">
      <div className="fields">
        <label>Name<input value={name} onChange={(e) => setName(e.target.value)} /></label>
        <label>Start date
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </label>
        <label>Target days
          <input type="number" min="1" value={targetDays}
                 onChange={(e) => setTargetDays(e.target.value)} />
        </label>
        <label>Distance (miles)
          <input type="number" min="0.1" step="0.01" value={distanceMiles}
                 readOnly={routeId !== ''}
                 onChange={(e) => setDistanceMiles(e.target.value)} />
        </label>
        {/* Always shown, never hidden when the list is empty: a control that
            disappears when it has nothing to offer is indistinguishable from a
            feature that was never built. */}
        <label>Route
          <select value={routeId} onChange={(e) => applyRoute(e.target.value)}>
            <option value="">No route</option>
            {routes.map((route) => (
              <option key={route.routeId} value={route.routeId}>
                {route.name} · {fmt.miles(route.distanceMiles, 0)}
              </option>
            ))}
          </select>
        </label>
        {routes.length === 0 && (
          <p className="muted">No saved routes yet — build or import one on the Routes tab.</p>
        )}
        <label>Steps per mile
          <input type="number" min="1" value={stepsPerMile}
                 onChange={(e) => setStepsPerMile(e.target.value)} />
        </label>
        <label>Steps per minute
          <input type="number" min="1" value={stepsPerMinute}
                 onChange={(e) => setStepsPerMinute(e.target.value)} />
        </label>
        <label>Status
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="active">Active</option>
            <option value="complete">Complete</option>
          </select>
        </label>
      </div>

      {routeId !== '' && (
        <p className="muted">
          Distance comes from the route, so the figure and the line always agree. A route shorter
          than the distance already walked will be refused.
        </p>
      )}

      <p className="muted">
        Steps per mile turns every recorded step count into distance, and steps per minute drives
        every time estimate. Changing either re-reads the whole history through the new figure.
      </p>

      {error && <p className="error">{error}</p>}
      <div className="dialog__actions">
        {saved && <span className="good">Saved</span>}
        <button className="primary" onClick={save} disabled={!valid || saving}>Save</button>
      </div>
    </Card>
  );
}

/// Links that show this walk to someone with no account.
///
/// The lifetimes on offer stop at "never", because a walk shared with family
/// is not a thing anyone wants to re-issue every fortnight. Every link can be
/// revoked, which is the control that actually matters.
const LIFETIMES: Array<{ label: string; hours: number | null }> = [
  { label: '4 hours', hours: 4 },
  { label: '24 hours', hours: 24 },
  { label: '7 days', hours: 24 * 7 },
  { label: '30 days', hours: 24 * 30 },
  { label: 'Never expires', hours: null },
];

function SharesSection({ walkId }: { walkId: string }) {
  const [shares, setShares] = useState<ShareLink[]>([]);
  const [choice, setChoice] = useState('4');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const refresh = () => {
    void api.shares(walkId).then(setShares).catch(() => setShares([]));
  };
  useEffect(refresh, [walkId]);

  const urlFor = (token: string) => `${window.location.origin}/shared/${token}`;

  async function create() {
    setBusy(true);
    setError(null);
    try {
      const hours = choice === 'never' ? null : Number(choice);
      await api.createShare(walkId, hours);
      refresh();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  async function revoke(token: string) {
    setError(null);
    try {
      await api.revokeShare(token);
      refresh();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
    }
  }

  async function copy(token: string) {
    try {
      await navigator.clipboard.writeText(urlFor(token));
      setCopied(token);
      window.setTimeout(() => setCopied(null), 2000);
    } catch {
      // Clipboard access can be refused; the link is on screen to select.
      setError('Could not copy. The link is shown above — select and copy it.');
    }
  }

  return (
    <Card wide title="Share this walk">
      <p className="muted">
        A link anyone can open without an account. It shows where you have got to, your
        mileage, and the map. It does not show your pace, your cadence, or your step
        counts — nothing that says whether you are walking right now.
      </p>

      <div className="fields">
        <label>Lasts for
          <select value={choice} onChange={(e) => setChoice(e.target.value)}>
            {LIFETIMES.map((option) => (
              <option key={option.label} value={option.hours === null ? 'never' : String(option.hours)}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <button className="primary" onClick={() => void create()} disabled={busy}>
          {busy ? 'Creating…' : 'Create link'}
        </button>
      </div>

      {error && <p className="error">{error}</p>}

      {shares.length === 0 ? (
        <p className="muted">No links yet.</p>
      ) : (
        <div className="shares">
          {shares.map((share) => (
            <div key={share.token} className="shares__row">
              <div>
                <p className="shares__url">{urlFor(share.token)}</p>
                <p className="muted">
                  {share.expiresAt
                    ? `Until ${fmt.mediumDate(new Date(share.expiresAt * 1000).toISOString().slice(0, 10))}`
                    : 'Never expires'}
                </p>
              </div>
              <div className="routes__actions">
                <button onClick={() => void copy(share.token)}>
                  {copied === share.token ? 'Copied' : 'Copy'}
                </button>
                <button onClick={() => void revoke(share.token)}>Revoke</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function NewWalkDialog({ store, onClose }: { store: Store; onClose: () => void }) {
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState(fmt.todayKey());
  const [targetDays, setTargetDays] = useState('365');
  const [distanceMiles, setDistanceMiles] = useState('');
  const [override, setOverride] = useState(false);
  const [stepsPerMile, setStepsPerMile] = useState(String(store.walk?.stepsPerMile ?? ''));
  const [stepsPerMinute, setStepsPerMinute] = useState(String(store.walk?.stepsPerMinute ?? ''));
  const [completePrevious, setCompletePrevious] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [routes, setRoutes] = useState<Route[]>([]);
  const [routeId, setRouteId] = useState('');

  // Failing to load routes must not block starting a walk by hand, so the
  // error is swallowed and the picker simply stays empty.
  useEffect(() => {
    void api.routes().then(setRoutes).catch(() => setRoutes([]));
  }, []);

  /// Choosing a route fills the distance in from its geometry. The server
  /// takes the figure from the route regardless, so the field is shown as
  /// read-only rather than left editable and quietly overridden.
  function chooseRoute(id: string) {
    setRouteId(id);
    const chosen = routes.find((route) => route.routeId === id);
    if (!chosen) return;
    setDistanceMiles(String(chosen.distanceMiles));
    if (name === '') setName(chosen.name);
  }

  const valid = name !== '' && Number(targetDays) >= 1 && Number(distanceMiles) >= 0.1
    && (!override || (Number(stepsPerMile) >= 1 && Number(stepsPerMinute) >= 1));

  async function create() {
    if (!valid) return;
    setSaving(true);
    setError(null);
    try {
      await api.createWalk({
        name,
        startDate,
        targetDays: Number(targetDays),
        distanceMiles: Number(distanceMiles),
        ...(routeId === '' ? {} : { routeId }),
        ...(override
          ? { stepsPerMile: Number(stepsPerMile), stepsPerMinute: Number(stepsPerMinute) }
          : {}),
        completePrevious,
      });
      await store.reload();
      onClose();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog title="Start a new walk" onClose={onClose}>
      <label>Name
        <input value={name} placeholder="Across America"
               onChange={(e) => setName(e.target.value)} />
      </label>
      <label>Start date
        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
      </label>
      <label>Route
        <select value={routeId} onChange={(e) => chooseRoute(e.target.value)}>
          <option value="">No route — enter a distance</option>
          {routes.map((route) => (
            <option key={route.routeId} value={route.routeId}>
              {route.name} · {fmt.miles(route.distanceMiles, 0)}
            </option>
          ))}
        </select>
      </label>
      {routes.length === 0 && (
        <p className="muted">No saved routes yet — build or import one on the Routes tab.</p>
      )}
      <label>Distance (miles)
        <input type="number" min="0.1" step="0.01" value={distanceMiles}
               readOnly={routeId !== ''}
               onChange={(e) => setDistanceMiles(e.target.value)} />
      </label>
      {routeId !== '' && (
        <p className="muted">Taken from the route, so the figure and the line always agree.</p>
      )}
      <label>Over (days)
        <input type="number" min="1" value={targetDays}
               onChange={(e) => setTargetDays(e.target.value)} />
      </label>

      <label className="inline">
        <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} />
        Set a new calibration
      </label>
      {override ? (
        <>
          <label>Steps per mile
            <input type="number" min="1" value={stepsPerMile}
                   onChange={(e) => setStepsPerMile(e.target.value)} />
          </label>
          <label>Steps per minute
            <input type="number" min="1" value={stepsPerMinute}
                   onChange={(e) => setStepsPerMinute(e.target.value)} />
          </label>
        </>
      ) : (
        <p className="muted">
          Calibration is carried over from your most recent walk — these are personal constants,
          not per-walk ones.
        </p>
      )}

      <label className="inline">
        <input type="checkbox" checked={completePrevious}
               onChange={(e) => setCompletePrevious(e.target.checked)} />
        Mark the current walk complete
      </label>

      {error && <p className="error">{error}</p>}
      <div className="dialog__actions">
        <button onClick={onClose}>Cancel</button>
        <button className="primary" onClick={create} disabled={!valid || saving}>Create</button>
      </div>
    </Dialog>
  );
}
